import React, {useEffect, useMemo, useState} from "react";
import {
    Avatar,
    Box,
    Button,
    Flex,
    Icon,
    Input,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useColorModeValue,
} from "@chakra-ui/react";
import {RepeatIcon} from "@chakra-ui/icons";
import {MdEmojiEvents, MdNotificationsActive} from "react-icons/md";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import {fetchTopSchoolsByBellUsage, LEADERBOARD_PERIODS, todayISO} from "./topSchoolsService";

// Medal colours for the podium positions (SN 1-3).
const rankAccent = (rank) => {
    if (rank === 1) return "yellow.400";
    if (rank === 2) return "gray.400";
    if (rank === 3) return "orange.400";
    return "transparent";
};

export default function TopSchools() {
    const textColor = useColorModeValue("gray.700", "white");
    const mutedColor = useColorModeValue("gray.500", "gray.400");
    const rowHover = useColorModeValue("gray.50", "whiteAlpha.100");
    const borderColor = useColorModeValue("gray.100", "whiteAlpha.200");

    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [period, setPeriod] = useState("YEARLY");
    const [date, setDate] = useState(todayISO());

    const activeBg = useColorModeValue("teal.400", "teal.300");
    const activeColor = useColorModeValue("white", "gray.800");
    const tabColor = useColorModeValue("gray.500", "gray.400");

    const loadData = async (nextPeriod = period, nextDate = date) => {
        setIsLoading(true);
        setError("");
        try {
            const top = await fetchTopSchoolsByBellUsage(10, nextPeriod, nextDate);
            setRows(top || []);
        } catch (e) {
            console.error("Top schools load failed:", e);
            setError("Could not load the top schools. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData(period, date);
    }, [period, date]);

    const maxBells = useMemo(
        () => rows.reduce((max, row) => Math.max(max, row.bellsPlayed || 0), 0),
        [rows]
    );

    // On the Daily tab we show completion as played/total instead of a relative share.
    const isDaily = period === "DAILY";

    return (
        <Card p="16px">
            <CardHeader p="12px 5px" mb="12px">
                <Flex direction="column" w="100%" gap="12px">
                    <Flex justify="space-between" align="center" w="100%" wrap="wrap" gap="8px">
                        <Box>
                            <Flex align="center" gap="8px">
                                <Icon as={MdEmojiEvents} color="teal.300" w="22px" h="22px"/>
                                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                                    Top 10 Schools
                                </Text>
                            </Flex>
                            <Text fontSize="sm" color={mutedColor}>
                                Ranked by number of bells played
                            </Text>
                        </Box>
                        <Button
                            size="sm"
                            variant="ghost"
                            leftIcon={<RepeatIcon/>}
                            onClick={() => loadData()}
                            isLoading={isLoading}
                        >
                            Refresh
                        </Button>
                    </Flex>
                    <Flex
                        justify="space-between"
                        align="center"
                        w="100%"
                        wrap="wrap"
                        gap="8px"
                    >
                        <Flex
                            align="center"
                            gap="4px"
                            bg={rowHover}
                            borderRadius="full"
                            p="4px"
                            flex={{base: "1 1 100%", md: "initial"}}
                        >
                            {LEADERBOARD_PERIODS.map((p) => {
                                const isActive = p.key === period;
                                return (
                                    <Button
                                        key={p.key}
                                        size="sm"
                                        flex={{base: "1", md: "initial"}}
                                        variant="ghost"
                                        borderRadius="full"
                                        fontSize="sm"
                                        fontWeight="600"
                                        color={isActive ? activeColor : tabColor}
                                        bg={isActive ? activeBg : "transparent"}
                                        _hover={{bg: isActive ? activeBg : rowHover}}
                                        _active={{bg: isActive ? activeBg : rowHover}}
                                        isDisabled={isLoading && !isActive}
                                        onClick={() => setPeriod(p.key)}
                                    >
                                        {p.label}
                                    </Button>
                                );
                            })}
                        </Flex>
                        <Input
                            type="date"
                            size="sm"
                            width={{base: "100%", md: "170px"}}
                            borderRadius="md"
                            fontSize="sm"
                            value={date}
                            max={todayISO()}
                            isDisabled={isLoading}
                            onChange={(e) => setDate(e.target.value || todayISO())}
                        />
                    </Flex>
                </Flex>
            </CardHeader>
            <CardBody>
                <Box w="100%" overflowX="auto">
                    {isLoading ? (
                        <Flex justify="center" align="center" py="60px">
                            <Spinner color="teal.300" size="lg"/>
                        </Flex>
                    ) : error ? (
                        <Flex direction="column" align="center" py="50px" gap="12px">
                            <Text color="red.400" fontSize="sm">{error}</Text>
                            <Button size="sm" onClick={() => loadData()} leftIcon={<RepeatIcon/>}>Retry</Button>
                        </Flex>
                    ) : rows.length === 0 ? (
                        <Flex direction="column" align="center" py="50px">
                            <Text color={mutedColor} fontSize="sm">
                                No bell usage recorded for the selected period yet.
                            </Text>
                        </Flex>
                    ) : (
                        <Table variant="simple" color={textColor}>
                            <Thead>
                                <Tr my=".8rem" ps="0px">
                                    <Th ps="0px" color="gray.400" borderColor={borderColor}>SN</Th>
                                    <Th color="gray.400" borderColor={borderColor}>School Details</Th>
                                    <Th color="gray.400" borderColor={borderColor} isNumeric>Bells Played</Th>
                                    <Th color="gray.400" borderColor={borderColor} isNumeric>Days Played</Th>
                                    <Th color="gray.400" borderColor={borderColor}>Address</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {rows.map((row, index) => {
                                    const rank = index + 1;
                                    const share = maxBells ? Math.round((row.bellsPlayed / maxBells) * 100) : 0;
                                    const totalBells = row.totalBells || 0;
                                    // Daily completion: how much of today's scheduled bells actually played.
                                    const completion = totalBells
                                        ? Math.min(100, Math.round((row.bellsPlayed / totalBells) * 100))
                                        : 0;
                                    const completionColor =
                                        completion >= 100 ? "green.400" : completion >= 50 ? "teal.300" : "orange.300";
                                    return (
                                        <Tr
                                            key={row.schoolId ?? `${row.name}-${index}`}
                                            _hover={{bg: rowHover}}
                                            transition="background 0.15s ease"
                                        >
                                            <Td ps="0px" borderColor={borderColor}>
                                                <Flex
                                                    align="center"
                                                    justify="center"
                                                    w="26px"
                                                    h="26px"
                                                    borderRadius="full"
                                                    fontWeight="bold"
                                                    fontSize="sm"
                                                    color={rank <= 3 ? "white" : mutedColor}
                                                    bg={rank <= 3 ? rankAccent(rank) : "transparent"}
                                                >
                                                    {rank}
                                                </Flex>
                                            </Td>
                                            <Td borderColor={borderColor}>
                                                <Flex align="center" gap="12px" minW="200px">
                                                    <Avatar
                                                        size="sm"
                                                        name={row.name}
                                                        src={row.logo || undefined}
                                                        borderRadius="12px"
                                                    />
                                                    <Text fontSize="sm" color={textColor} fontWeight="bold" noOfLines={1}>
                                                        {row.name}
                                                    </Text>
                                                </Flex>
                                            </Td>
                                            <Td borderColor={borderColor} isNumeric>
                                                {isDaily ? (
                                                    <Tooltip
                                                        hasArrow
                                                        placement="top"
                                                        label={`${completion}% played — ${Number(row.bellsPlayed).toLocaleString()} of ${Number(totalBells).toLocaleString()} bells today`}
                                                    >
                                                        <Flex direction="column" align="flex-end" gap="6px" minW="130px" cursor="default">
                                                            <Flex align="baseline" gap="4px">
                                                                <Icon as={MdNotificationsActive} w="14px" h="14px" color={completionColor}/>
                                                                <Text fontSize="md" fontWeight="bold" color={textColor}>
                                                                    {Number(row.bellsPlayed).toLocaleString()}
                                                                </Text>
                                                                <Text fontSize="sm" color={mutedColor}>
                                                                    /{Number(totalBells).toLocaleString()}
                                                                </Text>
                                                            </Flex>
                                                            <Box w="100%" h="6px" bg={rowHover} borderRadius="full" overflow="hidden">
                                                                <Box
                                                                    h="100%"
                                                                    w={`${completion}%`}
                                                                    bg={completionColor}
                                                                    borderRadius="full"
                                                                    transition="width 0.3s ease"
                                                                />
                                                            </Box>
                                                        </Flex>
                                                    </Tooltip>
                                                ) : (
                                                    <Flex direction="column" align="flex-end" gap="6px" minW="110px">
                                                        <Flex align="center" gap="6px">
                                                            <Icon as={MdNotificationsActive} w="14px" h="14px" color="teal.400"/>
                                                            <Text fontSize="md" fontWeight="bold" color={textColor}>
                                                                {Number(row.bellsPlayed).toLocaleString()}
                                                            </Text>
                                                            <Text fontSize="xs" color={mutedColor}>bells</Text>
                                                        </Flex>
                                                        <Box w="100%" h="4px" bg={rowHover} borderRadius="full" overflow="hidden">
                                                            <Box h="100%" w={`${share}%`} bg="teal.300" borderRadius="full"/>
                                                        </Box>
                                                    </Flex>
                                                )}
                                            </Td>
                                            <Td borderColor={borderColor} isNumeric>
                                                <Text fontSize="md" fontWeight="bold" color={textColor}>
                                                    {Number(row.daysPlayed).toLocaleString()}
                                                </Text>
                                                <Text fontSize="xs" color={mutedColor}>days</Text>
                                            </Td>
                                            <Td borderColor={borderColor}>
                                                <Text fontSize="sm" color={mutedColor} noOfLines={2} maxW="220px">
                                                    {row.address}
                                                </Text>
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    )}
                </Box>
            </CardBody>
        </Card>
    );
}
